import React, { useState } from "react";
import {
  Tabs,
  Button,
  Table,
  Popconfirm,
  Card,
  Input,
  Form,
  Modal,
  InputNumber,
  Select,
  Tag,
  Space,
  Badge,
  Statistic,
  Row,
  Col,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  AppstoreOutlined,
  InboxOutlined,
  StockOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  HistoryOutlined,
  EyeOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useFacilities, useGetSuppliers } from "../../../hooks/useFacilities";
import { useExpenses } from "../../../hooks/useExpenses";
import { useNavigate } from "react-router-dom";

const { TabPane } = Tabs;

const FacilitiesManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("categories");
  const [categoryForm] = Form.useForm();
  const [facilityForm] = Form.useForm();
  const [produceForm] = Form.useForm();
  const [consumeForm] = Form.useForm();
  const [expenseForm] = Form.useForm();

  const {
    useGetCategories,
    useCreateCategory,
    useUpdateCategory,
    useDeleteCategory,
    useGetFacilities,
    useCreateFacility,
    useUpdateFacility,
    useDeleteFacility,
    useProduceFacility,
    useConsumeFacility,
  } = useFacilities();

  const {
    useGetExpenses,
    useCreateExpense,
    useUpdateExpense,
    useDeleteExpense,
  } = useExpenses();

  // Get suppliers separately to avoid conditional hook call
  const { data: suppliers = [] } = useGetSuppliers();

  // Categories State
  const { data: categories = [], isLoading: categoriesLoading } =
    useGetCategories();
  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categorySearchText, setCategorySearchText] = useState("");

  // Facilities State
  const [facilityFilters, setFacilityFilters] = useState({
    PageNumber: 1,
    PageSize: 10,
    Name: "",
    CategoryId: null,
  });
  const { data: facilitiesData, isLoading: facilitiesLoading } =
    useGetFacilities(facilityFilters);
  const createFacilityMutation = useCreateFacility();
  const updateFacilityMutation = useUpdateFacility();
  const deleteFacilityMutation = useDeleteFacility();
  const [isFacilityModalVisible, setIsFacilityModalVisible] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);

  // Produce/Consume State
  const [isProduceModalVisible, setIsProduceModalVisible] = useState(false);
  const [isConsumeModalVisible, setIsConsumeModalVisible] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const produceFacilityMutation = useProduceFacility();
  const consumeFacilityMutation = useConsumeFacility();

  // Expenses State
  const [expenseFilters, setExpenseFilters] = useState({
    PageNumber: 1,
    PageSize: 10,
  });
  const { data: expensesData, isLoading: expensesLoading } =
    useGetExpenses(expenseFilters);
  const createExpenseMutation = useCreateExpense();
  const updateExpenseMutation = useUpdateExpense();
  const deleteExpenseMutation = useDeleteExpense();
  const [isExpenseModalVisible, setIsExpenseModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Category Handlers
  const handleAddCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    setIsCategoryModalVisible(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    categoryForm.setFieldsValue({ name: category.name });
    setIsCategoryModalVisible(true);
  };

  const handleCategorySubmit = async () => {
    try {
      const values = await categoryForm.validateFields();
      if (editingCategory) {
        await updateCategoryMutation.mutateAsync({
          id: editingCategory.id,
          ...values,
        });
      } else {
        await createCategoryMutation.mutateAsync(values);
      }
      setIsCategoryModalVisible(false);
    } catch (err) {
      console.error("Validation failed:", err);
    }
  };

  // Facility Handlers
  const handleAddFacility = () => {
    setEditingFacility(null);
    facilityForm.resetFields();
    setIsFacilityModalVisible(true);
  };

  const handleEditFacility = (facility) => {
    setEditingFacility(facility);
    facilityForm.setFieldsValue({
      name: facility.name,
      categoryId: facility.categoryId,
    });
    setIsFacilityModalVisible(true);
  };

  const handleFacilitySubmit = async () => {
    try {
      const values = await facilityForm.validateFields();
      if (editingFacility) {
        await updateFacilityMutation.mutateAsync({
          id: editingFacility.id,
          ...values,
        });
      } else {
        await createFacilityMutation.mutateAsync(values);
      }
      setIsFacilityModalVisible(false);
    } catch (err) {
      console.error("Validation failed:", err);
    }
  };

  // Produce Handlers
  const handleProduceClick = (facility) => {
    setSelectedFacility(facility);
    produceForm.resetFields();
    setIsProduceModalVisible(true);
  };

  const handleProduceSubmit = async () => {
    try {
      const values = await produceForm.validateFields();
      await produceFacilityMutation.mutateAsync({
        facilityId: selectedFacility.id,
        ...values,
      });
      setIsProduceModalVisible(false);
      produceForm.resetFields();
    } catch (err) {
      console.error("Production failed:", err);
    }
  };

  // Consume Handlers
  const handleConsumeClick = (facility) => {
    setSelectedFacility(facility);
    consumeForm.resetFields();
    setIsConsumeModalVisible(true);
  };

  const handleConsumeSubmit = async () => {
    try {
      const values = await consumeForm.validateFields();
      await consumeFacilityMutation.mutateAsync({
        facilityId: selectedFacility.id,
        amount: values.amount,
      });
      setIsConsumeModalVisible(false);
      consumeForm.resetFields();
    } catch (err) {
      console.error("Consumption failed:", err);
    }
  };

  // Expense Handlers
  const handleAddExpense = () => {
    setEditingExpense(null);
    expenseForm.resetFields();
    setIsExpenseModalVisible(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    expenseForm.setFieldsValue({
      title: expense.title,
      price: expense.price,
    });
    setIsExpenseModalVisible(true);
  };

  const handleExpenseSubmit = async () => {
    try {
      const values = await expenseForm.validateFields();
      if (editingExpense) {
        await updateExpenseMutation.mutateAsync({
          id: editingExpense.id,
          ...values,
        });
      } else {
        await createExpenseMutation.mutateAsync(values);
      }
      setIsExpenseModalVisible(false);
    } catch (err) {
      console.error("Validation failed:", err);
    }
  };

  // View Details Handler
  const handleViewDetails = (facility) => {
    navigate(`/facilities/${facility.id}`);
  };

  // Filter categories by search
  const filteredCategories = categories.filter((cat) =>
    cat.name?.toLowerCase().includes(categorySearchText.toLowerCase()),
  );

  // Category Columns
  const categoryColumns = [
    {
      title: <span className="text-white font-semibold">#</span>,
      dataIndex: "id",
      width: 80,
      render: (id) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary_hover rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-bold">{id}</span>
          </div>
        </div>
      ),
    },
    {
      title: <span className="text-white font-semibold">Category Name</span>,
      dataIndex: "name",
      render: (text) => (
        <Space>
          <span className="font-semibold text-gray-800 text-base">{text}</span>
        </Space>
      ),
    },
    {
      title: <span className="text-white font-semibold">Actions</span>,
      width: 180,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditCategory(record)}
            className="bg-primary text-white border-0 hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg transition-all"
          />
          <Popconfirm
            title={<span className="font-semibold">Delete Category?</span>}
            description="This will affect all facilities under this category."
            onConfirm={() => deleteCategoryMutation.mutate(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, className: "shadow-md" }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              className="shadow-md hover:shadow-lg transition-all"
              loading={
                deleteCategoryMutation.isPending &&
                deleteCategoryMutation.variables === record.id
              }
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Facility Columns
  const facilityColumns = [
    {
      title: <span className="text-white font-semibold">Facility Name</span>,
      dataIndex: "name",
      width: 120,
      render: (text) => (
        <Space>
          <span className="font-semibold text-gray-800 text-base">{text}</span>
        </Space>
      ),
    },
    {
      title: <span className="text-white font-semibold">Category</span>,
      dataIndex: "categoryName",
      width: 100,

      render: (text) => (
        <Tag
          color="cyan"
          className="px-4 py-2 text-sm font-medium rounded-lg border-0 shadow-sm"
        >
          <AppstoreOutlined className="mr-1" />
          {text}
        </Tag>
      ),
    },
    {
      title: <span className="text-white font-semibold">Initial Stock</span>,
      dataIndex: "initAmount",
      align: "center",
      width: 120,
      render: (amount) => (
        <div className="flex flex-col items-center">
          <div className="px-4 py-2 rounded-lg shadow-sm">
            <span className="text-blue-700 font-bold text-lg">{amount}</span>
          </div>
        </div>
      ),
    },
    {
      title: <span className="text-white font-semibold">Current Stock</span>,
      dataIndex: "currentAmount",
      align: "center",
      width: 140,
      render: (amount, record) => {
        const percentage =
          record.initAmount > 0 ? (amount / record.initAmount) * 100 : 0;
        let textColor = "text-green-700";

        if (percentage < 30) {
          textColor = "text-red-700";
        } else if (percentage < 60) {
          textColor = "text-orange-700";
        }

        return (
          <div className="flex flex-col items-center gap-1">
            <div className="px-4 py-2 rounded-lg shadow-sm">
              <span className={`${textColor} font-bold text-lg`}>{amount}</span>
            </div>
            {/* <div className="text-xs text-gray-500 font-medium">
                            {percentage.toFixed(0)}% remaining
                        </div> */}
          </div>
        );
      },
    },
    {
      title: <span className="text-white font-semibold">Status</span>,
      dataIndex: "currentAmount",
      width: 130,
      align: "center",
      render: (amount) => {
        if (amount === 0) {
          return (
            <Tag
              color="error"
              className="px-3 py-1 text-sm font-semibold rounded-lg border-0 shadow-sm"
            >
              Out of Stock
            </Tag>
          );
        } else if (amount < 5) {
          return (
            <Tag
              color="warning"
              className="px-3 py-1 text-sm font-semibold rounded-lg border-0 shadow-sm"
            >
              Low Stock
            </Tag>
          );
        }
        return (
          <Tag
            color="success"
            className="px-3 py-1 text-sm font-semibold rounded-lg border-0 shadow-sm"
          >
            In Stock
          </Tag>
        );
      },
    },
    {
      title: <span className="text-white font-semibold">Actions</span>,
      width: 280,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Produce ">
            <Button
              icon={<PlusCircleOutlined className="text-primary"/>}
              onClick={() => handleProduceClick(record)}
              className="border border-primary   shadow-md hover:shadow-lg transition-all"
            />
          </Tooltip>
          <Tooltip title="Consume ">
            <Button
              icon={<MinusCircleOutlined className="text-red-500 primary" />}
              onClick={() => handleConsumeClick(record)}
              className="border  border-red-600 shadow-md hover:shadow-lg transition-all"
              disabled={record.currentAmount === 0}
            />
          </Tooltip>
          <Tooltip title="View Details & History">
            <Button
              icon={<EyeOutlined  className="text-primary"/>}
              onClick={() => handleViewDetails(record)}
              className="border border-primary text-primary  shadow-md hover:shadow-lg transition-all"
            />
          </Tooltip>
          <Button
            icon={<EditOutlined  className="text-primary"/>}
            onClick={() => handleEditFacility(record)}
            className="border border-primary text-primary   shadow-md hover:shadow-lg transition-all"
          />
          <Popconfirm
            title={<span className="font-semibold">Delete Facility?</span>}
            description="Are you sure you want to delete this facility?"
            onConfirm={() => deleteFacilityMutation.mutate(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, className: "shadow-md" }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              className="shadow-md hover:shadow-lg transition-all"
              loading={
                deleteFacilityMutation.isPending &&
                deleteFacilityMutation.variables === record.id
              }
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Expense Columns
  const expenseColumns = [
    {
      title: <span className="text-white font-semibold">#</span>,
      dataIndex: "id",
      width: 80,
      render: (id) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary_hover rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-bold">{id}</span>
          </div>
        </div>
      ),
    },
    {
      title: <span className="text-white font-semibold">Title</span>,
      dataIndex: "title",
      width: 240,

      render: (text) => (
        <Space>
          <span className="font-semibold text-gray-800 text-base">{text}</span>
        </Space>
      ),
    },
    {
      title: <span className="text-white font-semibold">Price</span>,
      dataIndex: "price",
      width: 200,
      render: (price) => (
        <div className="flex">
            <span className="text-green-700 font-bold text-base">
              EGP {price?.toFixed(2)}
            </span>
        </div>
      ),
    },
    {
      title: <span className="text-white font-semibold">Created At</span>,
      dataIndex: "createdAt",
      width: 200,
      render: (date) => (
        <span className="text-gray-600">
          {new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
          })}
        </span>
      ),
    },
    {
      title: <span className="text-white font-semibold">Actions</span>,
      width: 180,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditExpense(record)}
            className="bg-primary text-white border-0 hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg transition-all"
          />
          <Popconfirm
            title={<span className="font-semibold">Delete Expense?</span>}
            description="Are you sure you want to delete this expense?"
            onConfirm={() => deleteExpenseMutation.mutate(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, className: "shadow-md" }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              className="shadow-md hover:shadow-lg transition-all"
              loading={
                deleteExpenseMutation.isPending &&
                deleteExpenseMutation.variables === record.id
              }
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="">
      {/* Main Content Card */}
      <Card className="border-0 rounded-2xl overflow-hidden">
        <style>
          {`  
                        .ant-table-thead > tr > th {
                            background: linear-gradient(135deg, #1F6F7D 0%, #1F6F7D 100%) !important;
                            color: white !important;
                            font-weight: 600 !important;
                            border: none !important;
                            padding: 16px !important;
                            font-size: 14px !important;
                        }
                        .ant-table-thead > tr > th::before {
                            display: none !important;
                        }
                        .ant-table-tbody > tr > td {
                            padding: 16px !important;
                            border-bottom: 1px solid #f0f0f0 !important;
                        }
                    `}
        </style>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
          tabBarStyle={{
            marginBottom: 24,
            borderBottom: "2px solid #e8e8e8",
          }}
        >
          {/* Categories Tab */}
          <TabPane
            tab={
              <span className="md:px-4 md:py-2 font-semibold text-base">
                <AppstoreOutlined className="mr-2" />
                Categories
              </span>
            }
            key="categories"
          >
            <div className="mb-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <Input
                  placeholder="Search categories..."
                  prefix={<SearchOutlined className="text-gray-400" />}
                  value={categorySearchText}
                  onChange={(e) => setCategorySearchText(e.target.value)}
                  size="large"
                  className="max-w-md shadow-md rounded-xl border-2 border-gray-200 hover:border-primary focus:border-primary transition-all"
                  allowClear
                />
                <Button
                  type=""
                  icon={<PlusOutlined />}
                  onClick={handleAddCategory}
                  size="large"
                  className="bg-gradient-to-r text-white from-primary to-primary_hover hover:from-primary_hover hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
                >
                  Add New Category
                </Button>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-lg">
              <Table
                columns={categoryColumns}
                dataSource={filteredCategories}
                loading={categoriesLoading}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showTotal: (total) => `Total ${total} categories`,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                  className: "px-4 py-4",
                }}
              />
            </div>
          </TabPane>

          {/* Facilities Tab */}
          <TabPane
            tab={
              <span className="px-4 py-2 font-semibold text-base">
                <InboxOutlined className="mr-2" />
                Facilities
              </span>
            }
            key="facilities"
          >
            <div className="mb-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  <Input
                    placeholder="Search facilities..."
                    prefix={<SearchOutlined className="text-gray-400" />}
                    value={facilityFilters.Name}
                    onChange={(e) =>
                      setFacilityFilters({
                        ...facilityFilters,
                        Name: e.target.value,
                        PageNumber: 1,
                      })
                    }
                    size="large"
                    className="max-w-md shadow-md rounded-xl border-2 border-gray-200 hover:border-primary focus:border-primary transition-all"
                    allowClear
                  />
                  <Select
                    placeholder="Filter by category"
                    value={facilityFilters.CategoryId}
                    onChange={(value) =>
                      setFacilityFilters({
                        ...facilityFilters,
                        CategoryId: value,
                        PageNumber: 1,
                      })
                    }
                    size="large"
                    className="w-full sm:w-64 shadow-md"
                    allowClear
                  >
                    {categories.map((cat) => (
                      <Select.Option key={cat.id} value={cat.id}>
                        <Space>
                          <AppstoreOutlined />
                          {cat.name}
                        </Space>
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <Button
                  type=""
                  icon={<PlusOutlined />}
                  onClick={handleAddFacility}
                  size="large"
                  className="bg-gradient-to-r text-white from-primary to-primary_hover hover:from-primary_hover hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
                >
                  Add New Facility
                </Button>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-lg">
              <Table
                columns={facilityColumns}
                dataSource={facilitiesData?.items || []}
                loading={facilitiesLoading}
                rowKey="id"
                scroll={{ x: 300 }}
                pagination={{
                  current: facilityFilters.PageNumber,
                  pageSize: facilityFilters.PageSize,
                  total: facilitiesData?.totalCount || 0,
                  showTotal: (total) => `Total ${total} facilities`,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                  className: "px-4 py-4",
                  onChange: (page, pageSize) => {
                    setFacilityFilters({
                      ...facilityFilters,
                      PageNumber: page,
                      PageSize: pageSize,
                    });
                  },
                }}
              />
            </div>
          </TabPane>

          {/* Expenses Tab */}
          <TabPane
            tab={
              <span className="px-4 py-2 font-semibold text-base">
                <DollarOutlined className="mr-2" />
                Expenses
              </span>
            }
            key="expenses"
          >
            <div className="mb-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex-1"></div>
                <Button
                  type=""
                  icon={<PlusOutlined />}
                  onClick={handleAddExpense}
                  size="large"
                  className="bg-gradient-to-r text-white from-primary to-primary_hover hover:from-primary_hover hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
                >
                  Add New Expense
                </Button>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-lg">
              <Table
                columns={expenseColumns}
                dataSource={expensesData?.items || []}
                loading={expensesLoading}
                rowKey="id"
                pagination={{
                  current: expenseFilters.PageNumber,
                  pageSize: expenseFilters.PageSize,
                  total: expensesData?.totalCount || 0,
                  showTotal: (total) => `Total ${total} expenses`,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                  className: "px-4 py-4",
                  onChange: (page, pageSize) => {
                    setExpenseFilters({
                      ...expenseFilters,
                      PageNumber: page,
                      PageSize: pageSize,
                    });
                  },
                }}
              />
            </div>
          </TabPane>
        </Tabs>
      </Card>

      {/* Category Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3 text-xl">
            <span className="font-bold">
              {editingCategory ? "Edit Category" : "Add New Category"}
            </span>
          </div>
        }
        open={isCategoryModalVisible}
        onCancel={() => setIsCategoryModalVisible(false)}
        onOk={handleCategorySubmit}
        confirmLoading={
          createCategoryMutation.isPending || updateCategoryMutation.isPending
        }
        okText={editingCategory ? "Update Category" : "Create Category"}
        cancelText="Cancel"
        okButtonProps={{
          className:
            "bg-gradient-to-r from-primary to-primary_hover border-0 shadow-md hover:shadow-lg",
        }}
        width={500}
        className="rounded-2xl"
      >
        <Form form={categoryForm} layout="vertical" className="mt-6">
          <Form.Item
            name="name"
            label={
              <span className="font-semibold text-base">Category Name</span>
            }
            rules={[{ required: true, message: "Please enter category name" }]}
          >
            <Input
              placeholder="Enter category name"
              size="large"
              className="rounded-lg"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Facility Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3 text-xl">
            <span className="font-bold">
              {editingFacility ? "Edit Facility" : "Add New Facility"}
            </span>
          </div>
        }
        open={isFacilityModalVisible}
        onCancel={() => setIsFacilityModalVisible(false)}
        onOk={handleFacilitySubmit}
        confirmLoading={
          createFacilityMutation.isPending || updateFacilityMutation.isPending
        }
        okText={editingFacility ? "Update Facility" : "Create Facility"}
        cancelText="Cancel"
        okButtonProps={{
          className:
            "bg-gradient-to-r from-primary to-primary_hover border-0 shadow-md hover:shadow-lg",
        }}
        width={500}
        className="rounded-2xl"
      >
        <Form form={facilityForm} layout="vertical" className="mt-6">
          <Form.Item
            name="name"
            label={
              <span className="font-semibold text-base">Facility Name</span>
            }
            rules={[{ required: true, message: "Please enter facility name" }]}
          >
            <Input
              placeholder="Enter facility name"
              size="large"
              prefix={<InboxOutlined className="text-gray-400" />}
              className="rounded-lg"
            />
          </Form.Item>
          <Form.Item
            name="categoryId"
            label={<span className="font-semibold text-base">Category</span>}
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select
              placeholder="Select category"
              size="large"
              showSearch
              optionFilterProp="children"
              className="rounded-lg"
            >
              {categories.map((cat) => (
                <Select.Option key={cat.id} value={cat.id}>
                  <Space>
                    <AppstoreOutlined />
                    {cat.name}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {!editingFacility && (
            <Form.Item
              name="initialAmount"
              label={
                <span className="font-semibold text-base">
                  Initial Stock Amount
                </span>
              }
              rules={[
                { required: true, message: "Please enter initial amount" },
              ]}
            >
              <InputNumber
                placeholder="Enter initial stock amount"
                min={0}
                size="large"
                style={{ width: "100%" }}
                prefix={<StockOutlined />}
                className="rounded-lg"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Produce Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3 text-xl">
            <PlusCircleOutlined className="text-green-600" />
            <span className="font-bold">
              Produce Facility - {selectedFacility?.name}
            </span>
          </div>
        }
        open={isProduceModalVisible}
        onCancel={() => setIsProduceModalVisible(false)}
        onOk={handleProduceSubmit}
        confirmLoading={produceFacilityMutation.isPending}
        okText="Add Stock"
        cancelText="Cancel"
        okButtonProps={{
          className:
            "bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg",
        }}
        width={500}
        className="rounded-2xl"
      >
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Current Stock:</span>
            <span className="text-2xl font-bold text-blue-700">
              {selectedFacility?.currentAmount || 0}
            </span>
          </div>
        </div>
        <Form form={produceForm} layout="vertical" className="mt-6">
          {suppliers && suppliers.length > 0 && (
            <Form.Item
              name="supplierId"
              label={<span className="font-semibold text-base">Supplier</span>}
              rules={[{ required: true, message: "Please select a supplier" }]}
            >
              <Select
                placeholder="Select supplier"
                size="large"
                showSearch
                optionFilterProp="children"
                className="rounded-lg"
              >
                {suppliers.map((supplier) => (
                  <Select.Option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            name="amount"
            label={
              <span className="font-semibold text-base">Amount to Add</span>
            }
            rules={[
              { required: true, message: "Please enter amount" },
              { type: "number", min: 1, message: "Amount must be at least 1" },
            ]}
          >
            <InputNumber
              placeholder="Enter amount to add"
              min={1}
              size="large"
              style={{ width: "100%" }}
              prefix={<PlusCircleOutlined />}
              className="rounded-lg"
            />
          </Form.Item>
          <Form.Item
            name="price"
            label={
              <span className="font-semibold text-base">Price per Unit</span>
            }
            rules={[
              { required: true, message: "Please enter price" },
              { type: "number", min: 0, message: "Price must be positive" },
            ]}
          >
            <InputNumber
              placeholder="Enter price per unit"
              min={0}
              precision={2}
              size="large"
              style={{ width: "100%" }}
              prefix="EGP"
              className="rounded-lg"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Consume Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3 text-xl">
            <MinusCircleOutlined className="text-orange-600" />
            <span className="font-bold">
              Consume Facility - {selectedFacility?.name}
            </span>
          </div>
        }
        open={isConsumeModalVisible}
        onCancel={() => setIsConsumeModalVisible(false)}
        onOk={handleConsumeSubmit}
        confirmLoading={consumeFacilityMutation.isPending}
        okText="Remove Stock"
        cancelText="Cancel"
        okButtonProps={{
          className:
            "bg-orange-600 hover:bg-orange-700 border-0 shadow-md hover:shadow-lg",
          danger: true,
        }}
        width={500}
        className="rounded-2xl"
      >
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Available Stock:</span>
            <span className="text-2xl font-bold text-blue-700">
              {selectedFacility?.currentAmount || 0}
            </span>
          </div>
        </div>
        <Form form={consumeForm} layout="vertical" className="mt-6">
          <Form.Item
            name="amount"
            label={
              <span className="font-semibold text-base">Amount to Remove</span>
            }
            rules={[
              { required: true, message: "Please enter amount" },
              { type: "number", min: 1, message: "Amount must be at least 1" },
              {
                validator: (_, value) => {
                  if (value && value > (selectedFacility?.currentAmount || 0)) {
                    return Promise.reject(
                      "Cannot consume more than available stock",
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              placeholder="Enter amount to remove"
              min={1}
              max={selectedFacility?.currentAmount || 0}
              size="large"
              style={{ width: "100%" }}
              prefix={<MinusCircleOutlined />}
              className="rounded-lg"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Expense Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3 text-xl">
            <DollarOutlined className="text-green-600" />
            <span className="font-bold">
              {editingExpense ? "Edit Expense" : "Add New Expense"}
            </span>
          </div>
        }
        open={isExpenseModalVisible}
        onCancel={() => setIsExpenseModalVisible(false)}
        onOk={handleExpenseSubmit}
        confirmLoading={
          createExpenseMutation.isPending || updateExpenseMutation.isPending
        }
        okText={editingExpense ? "Update Expense" : "Create Expense"}
        cancelText="Cancel"
        okButtonProps={{
          className:
            "bg-gradient-to-r from-primary to-primary_hover border-0 shadow-md hover:shadow-lg",
        }}
        width={500}
        className="rounded-2xl"
      >
        <Form form={expenseForm} layout="vertical" className="mt-6">
          <Form.Item
            name="title"
            label={
              <span className="font-semibold text-base">Expense Title</span>
            }
            rules={[{ required: true, message: "Please enter expense title" }]}
          >
            <Input
              placeholder="Enter expense title"
              size="large"
              className="rounded-lg"
            />
          </Form.Item>
          <Form.Item
            name="price"
            label={<span className="font-semibold text-base">Price</span>}
            rules={[
              { required: true, message: "Please enter price" },
              { type: "number", min: 0, message: "Price must be positive" },
            ]}
          >
            <InputNumber
              placeholder="Enter price"
              min={0}
              precision={2}
              size="large"
              style={{ width: "100%" }}
              prefix="EGP"
              className="rounded-lg"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FacilitiesManagement;
